import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { api, ApiError, type LlmUsageTotal } from "../../lib/api";
import { Badge, Select, Skeleton } from "../../components/ui/primitives";
import { cn } from "../../lib/utils";
import type { AuditSearch, SetSearch } from "./AuditLogPage";

// The audit log's "Ask usage" tab: Ask's tokens (the worker's llm_usage) by calendar month in UTC,
// the same months as the bill. Per org, or per person once an org is picked. Staff asks are left
// out of customer totals unless asked for. Tokens only: there are no prices yet.

/** How many months the pickers offer, this one included. */
const MONTHS_BACK = 12;

const currentMonth = () => new Date().toISOString().slice(0, 7);
const shiftMonth = (ym: string, n: number) => {
  const [y, m] = ym.split("-").map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 7);
};
const monthStart = (ym: string) => `${ym}-01T00:00:00.000Z`;
const monthLabel = (ym: string) =>
  new Date(monthStart(ym)).toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });

/** The months shown: what the URL says, or the last three. */
const range = (s: AuditSearch) => {
  const until = s.until ?? currentMonth();
  const since = s.since && s.since <= until ? s.since : shiftMonth(until, -2);
  return { since, until };
};

const total = (t: LlmUsageTotal) => t.inputTokens + t.outputTokens + t.cacheReadTokens + t.cacheWriteTokens;

export function AskUsageFilters({ search, set }: { search: AuditSearch; set: SetSearch }) {
  const { since, until } = range(search);
  const months = Array.from({ length: MONTHS_BACK }, (_, i) => shiftMonth(currentMonth(), -i));
  return (
    <>
      <label className="flex items-center gap-1.5 text-xs text-muted">
        From
        <Select value={since} onChange={(e) => set({ since: e.target.value })} aria-label="First month">
          {months.filter((m) => m <= until).map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </Select>
      </label>
      <label className="flex items-center gap-1.5 text-xs text-muted">
        To
        <Select value={until} onChange={(e) => set({ until: e.target.value })} aria-label="Last month">
          {months.filter((m) => m >= since).map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </Select>
      </label>
      <Select value={search.staffAsks ?? ""} onChange={(e) => set({ staffAsks: (e.target.value || undefined) as AuditSearch["staffAsks"] })} aria-label="Staff asks">
        <option value="">Customers only</option>
        <option value="include">Customers and staff</option>
        <option value="only">Staff only</option>
      </Select>
      {search.org && (
        <Badge tone="accent" className="h-8 px-2 text-xs">
          One org · {search.org}
          <button onClick={() => set({ org: undefined })} aria-label="Show every org" className="rounded hover:bg-accent/10"><X className="size-3" /></button>
        </Badge>
      )}
    </>
  );
}

export function AskUsageLog({ search, set }: { search: AuditSearch; set: SetSearch }) {
  const { since, until } = range(search);
  const usage = useQuery({
    queryKey: ["llm-usage", since, until, search.org, search.staffAsks],
    queryFn: () => api.llmUsage({ from: monthStart(since), to: monthStart(shiftMonth(until, 1)), orgId: search.org, staff: search.staffAsks }),
    retry: false,
  });
  const byPerson = Boolean(search.org);
  const totals = usage.data?.totals ?? [];
  const cell = "border-b border-border px-3 py-2 align-top";
  const num = cn(cell, "text-right tabular");
  const sum = (pick: (t: LlmUsageTotal) => number) => totals.reduce((n, t) => n + pick(t), 0);

  if (usage.isLoading) return <div className="space-y-2">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-9" />)}</div>;
  if (usage.error) {
    return (
      <p className="text-sm text-bad">
        {usage.error instanceof ApiError && usage.error.status === 403 ? "The audit log is for super users only." : usage.error.message}
      </p>
    );
  }
  if (!totals.length) return <p className="mt-16 text-center text-sm text-muted">No one asked anything in these months.</p>;
  return (
    <div className="overflow-x-auto rounded-[14px] border border-border bg-surface">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            {["Month", byPerson ? "Person" : "Org", "Asks", "Calls", "Input", "Output", "Cache read", "Cache write", "Tokens"].map((h, i) => (
              <th key={h} className={cn("border-b border-border px-3 py-2 text-xs font-medium whitespace-nowrap text-muted", i < 2 ? "text-left" : "text-right")}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {totals.map((t) => (
            <tr key={`${t.month}:${t.orgId}:${t.userId ?? ""}`} className="hover:bg-surface-2/60">
              <td className={cn(cell, "whitespace-nowrap text-muted")}>{monthLabel(t.month)}</td>
              <td className={cell}>
                {byPerson ? (
                  <>
                    {t.userName ?? t.userEmail ?? t.userId}
                    {t.staff && <Badge tone="accent" className="ml-1.5">Staff</Badge>}
                    <span className="block text-xs text-subtle">{t.userEmail && t.userName ? `${t.userEmail} · ` : ""}{t.userId}</span>
                  </>
                ) : (
                  <button onClick={() => set({ org: t.orgId })} title="This org's people" className="text-left hover:text-accent hover:underline">{t.orgId}</button>
                )}
              </td>
              <td className={num}>{t.asks.toLocaleString()}</td>
              <td className={num}>{t.calls.toLocaleString()}</td>
              <td className={num}>{t.inputTokens.toLocaleString()}</td>
              <td className={num}>{t.outputTokens.toLocaleString()}</td>
              <td className={num}>{t.cacheReadTokens.toLocaleString()}</td>
              <td className={num}>{t.cacheWriteTokens.toLocaleString()}</td>
              <td className={cn(num, "font-medium")}>{total(t).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-medium">
            <td className="px-3 py-2" colSpan={2}>All {byPerson ? "people" : "orgs"}, {since === until ? monthLabel(since) : `${monthLabel(since)} – ${monthLabel(until)}`}</td>
            <td className="px-3 py-2 text-right tabular">{sum((t) => t.asks).toLocaleString()}</td>
            <td className="px-3 py-2 text-right tabular">{sum((t) => t.calls).toLocaleString()}</td>
            <td className="px-3 py-2 text-right tabular">{sum((t) => t.inputTokens).toLocaleString()}</td>
            <td className="px-3 py-2 text-right tabular">{sum((t) => t.outputTokens).toLocaleString()}</td>
            <td className="px-3 py-2 text-right tabular">{sum((t) => t.cacheReadTokens).toLocaleString()}</td>
            <td className="px-3 py-2 text-right tabular">{sum((t) => t.cacheWriteTokens).toLocaleString()}</td>
            <td className="px-3 py-2 text-right tabular">{sum(total).toLocaleString()}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
